<template>
    <datepicker
	ref="datePicker"
    :date="value"
    :secondDate="secondValue"
    :insertSecondDate="insertSecondValue"
    :name="name"
    :refName="refName"
    :id="id"
    :language="language"
    :openDate="parsedOpenDate"
    :dayCellContent="dayCellContent"
    :fullMonthName="fullMonthName"
	:enabledDates="enabledDates"
	:monthsToShow="monthsToShow"
	:disabledMonthMessage="disabledMonthMessage"
    :disabledDates="parsedDisabledDates"
    :highlighted="highlighted"
    :mondayFirst="mondayFirst"
    :clearButton="clearButton"
    :clearButtonIcon="clearButtonIcon"
    :disabled="disabled"
    :useUtc="useUtc"
    :showCancelButton="showCancelButton"
    :cancelButtonText="cancelButtonText"
    :rightArrowIcon="rightArrowIcon"
    :leftArrowIcon="leftArrowIcon"
    :showOtherMonthDays="showOtherMonthDays"
    :setBackgroundOnWeekend="setBackgroundOnWeekend"
    :monthOnTheRight="monthOnTheRight"
	:rightPosition="rightPosition"
    :displayWeekDays="displayWeekDays"
    :disableWeekends="disableWeekends"
	@updateDate="$emit('updatedDate', $event)"
    @updatedSecondDate="$emit('updatedSecondDate', $event)"
	@close="$emit('close')">
    </datepicker>
</template>

<script setup>

/**
This component is an abstractor for the datepicker local plugin
in order to avoid passing more props than needed in every instance
we need to create.
We hold the component SCSS in here cause the scss will be specific for
this project, so it makes sense to keep t in the abstractor
*/
import { ref, computed } from 'vue'
import Datepicker from '@/assets/localPlugins/datepicker/Datepicker'

const props = defineProps({
    value: [String, Date], /* Date value of the datepicker */
    secondValue: [String, Date], /* Date value of the datepicker */
    insertSecondValue: Boolean, /* Prop to choose which value we are choosing when start/end date exist in same calendar */
    name: String, /* name of the picker */
    refName: String, /* used as a ref */
    id: String, /* picker id */
    language: Object, /* picker language */
    openDate: [String, Number, Date], /* date to open the picker at */
    dayCellContent: Function, /* render custom content instead of the normal cell string with a day */
    fullMonthName: { /* display the full name of the month */
        type: Boolean,
        default: () => true
    },
	enabledDates: Array, /* Array of dates we want to enabled */
	monthsToShow: Array, /* Array of months to show */
	disabledMonthMessage: String, /* to display next to disabled months */
    disabledDates: Object, /* list of disabled dates */
    /* There are other options, but this ones are the most common
    {
        to: new Date(2016, 0, 5), // Disable all dates up to specific date
        from: new Date(2016, 0, 26), // Disable all dates after specific date
    }
    */
    highlighted: Object, /* list of highlighted dates and if we include selectedDates
        {
            from: 2024-11-04T00:00:00.000Z,
            to: 2024-11-04T00:00:00.000Z,
            includeStartEndDates: true/false (optional behaves as false)
        }
    */
    mondayFirst: { /* if the calendar starts with monday , if false it will start on sunday */
        type: Boolean,
        default: () => false
    },
    clearButton: Boolean, /* button to clear the selected date */
    clearButtonIcon: String, /* icon to be displayed in the clearButton */
    disabled: Boolean, /* disable the picker completely , nothing will work */
    useUtc: Boolean, /* use utc format */
    showCancelButton: { /* display a button to cancel the selected date */
        type: Boolean,
        default: () => false
    },
    cancelButtonText: String, /* the text on the cancel button */
    rightArrowIcon: { /* the icon to be shown as right arrow in year/month selection */
        type: String,
        default: () => 'arrow-right'
    },
    leftArrowIcon: { /* the icon to be shown as left arrow in year/month selection */
        type: String,
        default: () => 'arrow-left'
    },
    showOtherMonthDays: { /* month and year selection swap position, month - year or year - month */
        type: Boolean,
        default: () => true
    },
    setBackgroundOnWeekend: { /* weekend has a different color */
        type: Boolean,
        default: () => true
    },
    monthOnTheRight: { /* month and year selection swap position, month - year or year - month */
        type: Boolean,
        default: () => false
    },
    displayWeekDays: {
        type: Boolean,
        default: true
    },
    disableWeekends: Boolean
})

const emit = defineEmits(['updatedDate', 'close', 'updatedSecondDate'])

const rightPosition = ref(0)
const datePicker = ref(null)

const parsedOpenDate = computed(() => {
	/* If we receive an open date, we will simply return it */
	if (props.openDate) return props.openDate
	/* Validating if we receive disabled dates prop */
	else if (props.disabledDates) {
		if (props.disabledDates.to && !isNaN(props.disabledDates.to.getTime())) {
			/* if our current date is inside enabled dates, we will open the calendar in it */
			if (new Date() > props.disabledDates.to) return null
			return props.disabledDates.to
		} else if (props.disabledDates.from && !isNaN(props.disabledDates.from.getTime())) {
			/* if our current date is inside enabled dates, we will open the calendar in it */
			if (new Date() < props.disabledDates.from) return null
			return props.disabledDates.from
		}
	}
	return null
})

const parsedDisabledDates = computed(() => {
	if (props.enabledDates && props.enabledDates.length) {
		return {
			to: props.enabledDates[0],
			from: props.enabledDates[props.enabledDates.length - 1]
		}
	}

	return props.disabledDates
})
</script>

<style lang="scss">
.rtl {
	direction: rtl;
}

.vdp-datepicker {
	position: relative;
	text-align: left;
	* {
		box-sizing: border-box;
	}
}

.vdp-datepicker__calendar {
	width: 296px; /* the width of the picker is not dynamic */

	position: relative;

	background: var(--secondary-light-4);
	border-radius: 8px;

	box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.16);

	/* HEADER */
	.date-picker-header {
		padding: 8px 8px 0 8px;

		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		line-height: 40px;
		box-sizing: border-box;

		.year-section {
			.name {
				@include H6-Medium;
			}
		}

		.month-section {
			.name {
				@include H5-Medium;
			}
		}

		.year-section, .month-section {
			width: 100%;

			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;

			.name {
				width: calc(100% - 24px);
				height: auto;

				color: var(--secondary-dark-3);
				text-align: center;
			}
		}
	}

	/* BODY */
	.calendar-body {
		padding: 0 8px 8px 8px;
		overflow: hidden;

		.flex-rtl {
			display: flex;
			width: inherit;
			flex-wrap: wrap;
		}

		.flex-style {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;

			.day-headers {
				width: 100%;

				display: flex;
				justify-content: space-between;

				margin: 8px 0;
				cursor: default;

				span {
					display: flex;
					justify-content: center;

					width: 40px;
					height: 16px;

					@include H7-Regular;
					line-height: 16px;

					color: var(--secondary);
				}
			}
		}

		.cell {
			width: 40px;
			height: 40px;

			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;

			margin-bottom: 8px;

			@include H7-Medium;
			color:var(--secondary-dark-2);

			border: 1px solid transparent;
			border-radius: 50%;

			z-index: 1;

			&:not(.blank):not(.disabled):not(.other-month) {
				&.day, &.month, &.year {
					cursor: pointer;
				}
			}

            span {
                width: 100%;
                height: 100%;
                padding: 8px;

                display: flex;
                align-items: center;
                justify-content: center;

                border-radius: 50%;
                vertical-align: middle;

                z-index: 2;
            }

			&.selected {
				color: var(--secondary-light-4);

                span {
                    background-color: var(--primary);
                }
			}

			@media (hover: hover) and (pointer: fine) {
				&:hover:not(.selected):not(.disabled):not(.other-month):not(.today):not(.blank)  {
                    span {
                        background-color: var(--button-secondary-state-hover);
                    }
				}
            }

			&.disabled, &.other-month {
				pointer-events: all;
				opacity: 0.24;
				cursor: default;
			}

			&.today {
				border-color: var(--primary);

				&.disabled {
					opacity: 0.24;
					cursor: default;
				}
			}

            &.highlighted {
				background-color: var(--primary-light-3);
                border-radius: 0;

                &.selected {
                    border-color: var(--primary-light-3);

                    &:before {
                        content: '';
                        width: 100%;
                        height: 100%;

                        background-color: var(--primary-light-3);
                        border-radius: 50% 0 0 50%;
                        position: absolute;
                        top: 0;
                        left: 0;
                        z-index: 1;
                    }
                }

                &.sat, &.highlight-end {
                    border-radius: 0 20px 20px 0;

                    &:before {
                        border-radius: 0 50% 50% 0;
                    }

                    &.sun {
                        border-radius: 50%;

                        &:before {
                            border-radius: 50%;
                        }
                    }
                }

                &.sun, &.highlight-start {
                    border-radius: 20px 0 0 20px;

                    &:before {
                        border-radius: 50% 0 0 50%;
                    }

                    &.sat {
                        border-radius: 50%;

                        &:before {
                            border-radius: 50%;
                        }
                    }
                }
            }
		}
	}
}
</style>
